import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import SplashScreen from './components/SplashScreen';
import AuthScreen from './components/AuthScreen';
import MapScreenWrapper from './components/MapScreenWrapper';
import AdminDashboard from './components/AdminDashboard';
import PorcelainThrone from './components/PorcelainThrone';
import ToiletTower from './components/ToiletTower';
import RoyalReviews from './components/RoyalReviews';
import DailyFlush from './components/DailyFlush';
import UserProfileView from './components/UserProfileView';
import QRLanding from './components/QRLanding';
import MarketingLanding from './components/MarketingLanding';
import SocialMedia from './components/SocialMedia';
import InAppInvite from './components/InAppInvite';
import AmbassadorForm from './components/AmbassadorForm';
import PrivacyPolicy from './components/PrivacyPolicy';
import AppDownloadBanner from './components/AppDownloadBanner';
import MobileAppDownloadModal from './components/MobileAppDownloadModal';
import DownloadRedirect from './components/DownloadRedirect';
import CompleteProfileScreen from './components/CompleteProfileScreen';
import AuthRedirector from './components/AuthRedirector'; // Import AuthRedirector
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { diagnoseRestroomVisibility } from './utils/diagnosticRestrooms';
import analytics from './utils/analytics';
import {
  normalizeReferralCode,
  savePendingReferralCode,
} from './utils/referralState';
import PageTracker from './components/PageTracker';
import GoogleSignInButton from './components/GoogleSignInButton';
import './App.css';

// Wrapper component to extract URL parameters for shared restroom links
function MapScreenWithParams() {
  const { restroomId } = useParams();
  const navigate = useNavigate();
  // Referral links share the /r/ namespace: an 8-char code segment is a
  // referral (restroom doc IDs are 20 chars), anything else is a restroom.
  // Capture first, then continue to the map.
  useEffect(() => {
    if (!restroomId) return;
    const code = normalizeReferralCode(restroomId);
    if (code) {
      savePendingReferralCode(code);
      analytics.track('referral_detected', {
        referral_code: code,
        source: 'deep_link',
      });
      navigate('/', { replace: true });
    }
  }, [restroomId, navigate]);
  if (restroomId && normalizeReferralCode(restroomId)) return null;
  console.log('MapScreenWithParams received restroomId:', restroomId);
  return <MapScreenWrapper sharedRestroomId={restroomId} />;
}

// Make diagnostic available in console
if (typeof window !== 'undefined') {
  window.diagnoseRestroomVisibility = diagnoseRestroomVisibility;
}

// Component to handle deep links to specific restrooms
function RestroomDeepLinkHandler() {
  const { restroomId } = useParams();
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log('[RestroomDeepLinkHandler] Triggered. restroomId:', restroomId, 'currentUser:', !!currentUser, 'loading:', loading);
    if (loading) return; // Wait for auth state to load

    if (currentUser && currentUser.profileComplete) {
      // User is logged in and profile is complete, redirect to app with restroomId
      console.log('[RestroomDeepLinkHandler] User logged in, navigating to /app with selectedRestroomId:', restroomId);
      navigate('/app', { state: { selectedRestroomId: restroomId }, replace: true });
    } else {
      // User not logged in or profile incomplete, store restroomId and redirect to auth
      console.log('[RestroomDeepLinkHandler] User not logged in, storing restroomId in sessionStorage:', restroomId);
      sessionStorage.setItem('redirectRestroomId', restroomId);
      navigate('/auth', { replace: true, state: { from: location } });
    }
  }, [currentUser, loading, restroomId, navigate, location]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading restroom link...</p>
      </div>
    );
  }

  return null; // Or a simple loading indicator
}

// Component to hold all protected routes under /app
function AppProtectedRoutes({ currentUser }) { // Receive currentUser as a prop
  // Add a defensive check, though ProtectedRoute should prevent this from being null
  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <Routes>
      <Route index element={<MapScreenWrapper />} /> {/* /app */}
      <Route path="admin" element={currentUser.isAdmin ? <AdminDashboard /> : <Navigate to="/app" />} />
      <Route path="porcelain-throne" element={<PorcelainThrone />} />
      <Route path="daily-flush" element={<DailyFlush />} />
      <Route path="toilet-tower" element={<ToiletTower />} />
      <Route path="porcelain-pyramid" element={<ToiletTower />} />
      <Route path="profile/:identifier" element={<UserProfileView />} />
      <Route path="royal-reviews" element={<RoyalReviews />} />
      <Route path="r/:restroomId" element={<MapScreenWithParams />} />
      {/* Add other /app routes here */}
    </Routes>
  );
}

// ProtectedRoute component to handle authentication and profile completion redirects
function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Optionally render a loading spinner or null while auth state is being determined
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading user session...</p>
      </div>
    );
  }

  if (!currentUser) {
    // Not logged in, redirect to auth screen
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  if (!currentUser.profileComplete && location.pathname !== '/complete-profile') {
    // Logged in but profile not complete, redirect to complete profile
    return <Navigate to="/complete-profile" replace state={{ from: location }} />;
  }

  // Logged in and profile complete (or on complete-profile page), render children
  return children;
}


function AppContent() {
  const [showSplash, setShowSplash] = useState(true);
  const { currentUser, loading } = useAuth();
  const { featureFlags } = useSettings();
  const location = useLocation();

  useEffect(() => {
    // Clear stale localStorage if version changed
    const CURRENT_VERSION = '6.16.0';
    const storedVersion = localStorage.getItem('crappermapper_version');
    
    if (storedVersion !== CURRENT_VERSION) {
      console.log(`Version change detected: ${storedVersion} → ${CURRENT_VERSION}. Clearing cache...`);
      
      // Clear all crappermapper localStorage except auth
      const keysToKeep = ['firebase_auth_token', 'firebase_user'];
      Object.keys(localStorage).forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear sessionStorage
      try {
        sessionStorage.clear();
        console.log('SessionStorage cleared');
      } catch (e) {
        console.error('Error clearing sessionStorage:', e);
      }
      
      // Set new version
      localStorage.setItem('crappermapper_version', CURRENT_VERSION);
      
      // Force reload after clearing (only once)
      if (!sessionStorage.getItem('cache_cleared_reload')) {
        sessionStorage.setItem('cache_cleared_reload', 'true');
        console.log('Forcing reload to clear all caches...');
        window.location.reload(true);
      }
    }
    
    // Show splash screen for 2 seconds
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (showSplash) {
    return <SplashScreen />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading The Crapper Mapper...</p>
      </div>
    );
  }

  return (
    <>
      <PageTracker />
      <AuthRedirector /> {/* Render AuthRedirector here */}
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={
              currentUser ? (
                currentUser.profileComplete ? <Navigate to="/app" /> : <Navigate to="/complete-profile" />
              ) : (
                <DataProvider>
                  <MarketingLanding />
                  {featureFlags.GoogleSignin && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <GoogleSignInButton />
                    </div>
                  )}
                </DataProvider>
              )
            } 
          />
          <Route path="/auth" element={<AuthScreen />} />
          <Route path="/qrcode" element={<QRLanding />} />
          <Route path="/download" element={<DownloadRedirect />} />
          <Route path="/socials" element={<SocialMedia />} />
          <Route path="/inappinvite" element={<InAppInvite />} />
          <Route path="/ambassador" element={<AmbassadorForm />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* New route for handling restroom deep links */}
          <Route path="/restroom/:restroomId" element={<RestroomDeepLinkHandler />} />

          {/* Profile Completion Route - also protected */}
          <Route 
            path="/complete-profile" 
            element={
              <ProtectedRoute>
                <CompleteProfileScreen />
              </ProtectedRoute>
            } 
          />

          {/* All /app routes are now protected */}
          <Route 
            path="/app/*" 
            element={
              <ProtectedRoute>
                <AppProtectedRoutes currentUser={currentUser} />
              </ProtectedRoute>
            } 
          />
        </Routes>
        <Toaster 
          position="top-center"
          containerStyle={{ pointerEvents: 'none' }}
          toastOptions={{
            duration: 3000,
            style: {
              background: '#614f48',
              color: '#fff',
              fontSize: '14px',
              padding: '12px 16px',
              borderRadius: '8px',
              maxWidth: '90vw',
              margin: '10px',
              zIndex: 9999,
              pointerEvents: 'auto',
            },
            mobile: {
              style: {
                fontSize: '16px',
                padding: '14px 18px',
                borderRadius: '10px',
                margin: '15px',
              }
            }
          }}
        />
      </div>
      <AppDownloadBanner />
      <MobileAppDownloadModal />
    </>
  );
}

function App() {
  return (
    <Router>
      <SettingsProvider>
        <AuthProvider>
          <DataProvider>
            <AppContent />
          </DataProvider>
        </AuthProvider>
      </SettingsProvider>
    </Router>
  );
}

export default App;